/**
 * Pending requests in Overview's Needs you, in the one order (ADR 0072): each answered where it
 * is, in the answer sheet (`to: { approval }`), never by going to the Inbox. Tool requests sit in
 * Requests (key a:), questions, plans and forms in Questions (key q:). A session that is waiting
 * because of a request shows once, as the request.
 */
import type { ApprovalRequest } from '@orgus/protocol';
import type { Entities } from '../../lib/store.ts';
import type { NeedItem } from '../overview/needsYou.ts';
import { sortPending } from './live.ts';
import { shownTitle } from '../sessions/shownTitle.ts';
import { isAnswerable } from './askModel.ts';

const RISK_WORD = { high: 'High risk', medium: 'Medium risk', low: 'Low risk' } as const;

export function approvalNeeds(pending: ApprovalRequest[], e: Pick<Entities, 'machines' | 'projects' | 'sessions'>): NeedItem[] {
  return sortPending(pending).map((a) => {
    const where = [a.projectId ? e.projects[a.projectId]?.name : null, e.machines[a.machineId]?.name ?? null].filter(Boolean).join(' on ');
    const s = e.sessions[a.sessionId];
    const session = s ? shownTitle(s).title : undefined;
    return {
      key: `${isAnswerable(a) ? 'q' : 'a'}:${a.id}`,
      kind: 'session',
      title: a.title,
      detail: `${RISK_WORD[a.risk]}. Approval needed${session ? ` in "${session}"` : ''}.`,
      where,
      at: a.createdAt,
      to: { approval: a.id },
      verb: 'Answer',
      order: { deadline: a.expiresAt, risk: a.risk },
    };
  });
}

/** Approvals first; a waiting session already covered by one of them is not listed twice. */
export function withApprovals(items: NeedItem[], pending: ApprovalRequest[], e: Pick<Entities, 'machines' | 'projects' | 'sessions'>): NeedItem[] {
  if (!pending.length) return items;
  const covered = new Set(pending.map((a) => `s:${a.sessionId}`));
  return [...approvalNeeds(pending, e), ...items.filter((i) => !covered.has(i.key))];
}
